import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCategories, useSaveCategory } from '@/hooks/useCategories'
import { useT } from '@/i18n/I18nProvider'
import type { Category, CategoryKind } from '@/types/db'

const NONE = '__none__'

export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  category?: Category | null
}) {
  const { t } = useT()
  const { data: all } = useCategories(true)
  const save = useSaveCategory()

  const [name, setName] = useState('')
  const [icon, setIcon] = useState('')
  const [kind, setKind] = useState<CategoryKind>('expense')
  const [parentId, setParentId] = useState<string>(NONE)

  useEffect(() => {
    if (!open) return
    setName(category?.name ?? '')
    setIcon(category?.icon ?? '')
    setKind(category?.kind ?? 'expense')
    setParentId(category?.parent_id ?? NONE)
  }, [open, category])

  // Pais possíveis: categorias-raiz do mesmo tipo (não pode ser ela mesma).
  const parentOptions: ComboOption[] = useMemo(() => {
    const roots = (all ?? []).filter((c) => c.parent_id === null && c.kind === kind && c.id !== category?.id)
    return [
      { value: NONE, label: t('mng.cat.noneParent') },
      ...roots.map((c) => ({ value: c.id, label: `${c.icon ?? ''} ${c.name}`.trim() })),
    ]
  }, [all, kind, category?.id, t])

  // Ao trocar o tipo (receita/despesa), um pai escolhido do outro tipo deixa de
  // existir nas opções — limpa para não salvar categoria com pai de outro tipo.
  useEffect(() => {
    if (parentId !== NONE && !parentOptions.some((o) => o.value === parentId)) {
      setParentId(NONE)
    }
  }, [parentId, parentOptions])

  // Uma categoria que já tem filhos não pode virar subcategoria.
  const hasChildren = useMemo(() => (all ?? []).some((c) => c.parent_id === category?.id), [all, category?.id])

  async function submit() {
    if (!name.trim()) return toast.error(t('mng.cat.nameRequired'))
    try {
      await save.mutateAsync({
        id: category?.id,
        values: {
          name: name.trim(),
          icon: icon.trim() || null,
          kind,
          parent_id: parentId === NONE ? null : parentId,
        },
      })
      toast.success(category ? t('mng.cat.updated') : t('mng.cat.created'))
      onOpenChange(false)
    } catch (e) {
      toast.error(t('mng.errSave'), { description: (e as Error).message })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{category ? t('mng.cat.editTitle') : t('mng.cat.new')}</DialogTitle>
          <DialogDescription>{t('mng.cat.dialogDesc')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label>{t('mng.cat.type')}</Label>
            <Tabs value={kind} onValueChange={(v) => setKind(v as CategoryKind)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="expense">{t('mng.cat.expense')}</TabsTrigger>
                <TabsTrigger value="income">{t('mng.cat.income')}</TabsTrigger>
                <TabsTrigger value="investment">{t('mng.cat.investment')}</TabsTrigger>
              </TabsList>
            </Tabs>
            {kind === 'investment' && (
              <p className="text-muted-foreground text-xs">
                {t('mng.cat.investNote1')}
                <b>{t('mng.cat.investNoteBold1')}</b>
                {t('mng.cat.investNote2')}
                <b>{t('mng.cat.investNoteBold2')}</b>
                {t('mng.cat.investNote3')}
              </p>
            )}
          </div>

          <div className="grid grid-cols-[80px_1fr] gap-3">
            <div className="grid gap-2">
              <Label htmlFor="cat-icon">{t('mng.cat.icon')}</Label>
              <Input id="cat-icon" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🛒" maxLength={4} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cat-name">{t('mng.cat.name')}</Label>
              <Input id="cat-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('mng.cat.namePlaceholder')} />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>{t('mng.cat.parent')}</Label>
            <Combobox
              options={parentOptions}
              value={parentId}
              onChange={setParentId}
              disabled={hasChildren}
              placeholder={t('mng.cat.noneParent')}
              searchPlaceholder={t('mng.cat.searchParent')}
            />
            {hasChildren && (
              <p className="text-muted-foreground text-xs">
                {t('mng.cat.hasChildrenNote')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
